import React, { useRef } from 'react';
import { Toast } from 'primereact/toast';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useSelector } from 'react-redux';
import { selectDealerId, selectSiteUseId } from '../../../store/slices/userParamsSlice';
import CustomerDefinitionDialog from './components/CustomerDefinitionDialog';
import CustomerDefinitionTable from './components/CustomerDefinitionTable';
import CustomerDefinitionFilter from './components/CustomerDefinitionFilter';
import useCustomerForm from './hooks/useCustomerForm';
import { useGetCustomersQuery, useAddCustomerMutation, useDeleteCustomerMutation, useUpdateCustomerMutation,useLazyExportCustomerQuery } from '../../../api/customerApi';
import { useLanguage } from '../../../contexts/LanguageContext';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';

const CustomerDefinitionPage = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const toast = useRef(null);
    const siteUseId = useSelector(selectSiteUseId);
    const dealerId = useSelector(selectDealerId);

    const [addCustomer, { isLoading: isAdding }] = useAddCustomerMutation();
    const [updateCustomer, { isLoading: isUpdating }] = useUpdateCustomerMutation();
    const [deleteCustomer, { isLoading: isDeleting }] = useDeleteCustomerMutation();

    const {
        formData,
        setFormData,
        errors,
        dialogVisible,
        editMode,
        filters: customerFilters,
        pagination,
        hasUnsavedChanges,
        handleNew,
        handleEdit,
        handleSave,
        handleDelete,
        handleClose,
        handleApplyFilters,
        handleResetFilters,
        handlePageChange,
        addChassis,
        removeChassis
    } = useCustomerForm({
        addCustomer,
        updateCustomer,
        deleteCustomer,
        toast,
        th,
        dealerId,
        siteUseId
    });

    const apiParams = {
        ...customerFilters,
        ...pagination
    };

    const { data: customersResponse = {}, isLoading } = useGetCustomersQuery(apiParams);
    const customers = customersResponse.items || [];
    const totalRecords = customersResponse.totalCount || 0;

    const confirmDelete = () => {
        confirmDialog({
            message: th('customerDefinition.deleteConfirmMessage'),
            header: th('customerDefinition.deleteConfirmHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: th('common.yes'),
            rejectLabel: th('common.no'),
            accept: handleDelete,
            reject: () => {
                toast.current.show({
                    severity: 'info',
                    summary: th('common.cancelled'),
                    detail: th('customerDefinition.deleteCancelled'),
                    life: 3000
                });
            },
            rejectClassName: 'p-button-outlined'
        });
    };

    const handleDialogHide = () => {
        if (hasUnsavedChanges) {
            confirmDialog({
                message: th('customerDefinition.unsavedChangesMessage'),
                header: th('customerDefinition.unsavedChangesHeader'),
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: th('common.yes'),
                rejectLabel: th('common.no'),
                accept: () => handleClose(true),
                reject: () => {},
                rejectClassName: 'p-button-outlined'
            });
        } else {
            handleClose();
        }
    };

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="flex justify-content-between align-items-center mb-4">
                <div className="flex align-items-center gap-2">
                    <h1 className="m-0">{th('customerDefinition.title')}</h1>
                </div>
                 <GenericFileExportButton
                    useExportQuery={useLazyExportCustomerQuery}
                    exportParams={{ ...apiParams, LanguageId: getCurrentLanguageId() }}
                    buttonLabel={th("common.export") || "Excel"}
                    buttonClassName="p-button-success"
                    icon="pi pi-file-excel"
                    fileNameExtractor={(result) => result?.fileName || "customers.xlsx"}
                    dataExtractor={(result) => result?.fileBase64}
                    contentTypeExtractor={(result) => result?.contentType}
                />
            </div>

            <CustomerDefinitionFilter handleApplyFilters={handleApplyFilters} handleResetFilters={handleResetFilters} />

            <CustomerDefinitionTable customers={customers} totalRecords={totalRecords} loading={isLoading} handleNew={handleNew} handleEdit={handleEdit} pagination={pagination} onPageChange={handlePageChange} />

            <CustomerDefinitionDialog
                visible={dialogVisible}
                onHide={handleDialogHide}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                handleSave={handleSave}
                editMode={editMode}
                handleDelete={confirmDelete}
                isSubmitting={isAdding || isUpdating}
                isDeleting={isDeleting}
                addChassis={addChassis}
                removeChassis={removeChassis}
            />
        </div>  
    );  
};

export default CustomerDefinitionPage;
